import { generateHtml } from './generateHtml';

const NETLIFY_API = 'https://api.netlify.com/api/v1';
const TOKEN = import.meta.env.VITE_NETLIFY_TOKEN;

export function nameToNetlifySlug(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

/**
 * Fetch wrapper with CORS proxy fallback for ad-blockers / browser restrictions.
 */
async function fetchNetlify(endpoint, options = {}) {
  const url = `${NETLIFY_API}${endpoint}`;
  const headers = {
    Authorization: `Bearer ${TOKEN}`,
    ...options.headers,
  };

  try {
    const res = await fetch(url, { ...options, headers });
    return res;
  } catch (err) {
    if (
      err.name === 'TypeError' ||
      err.message.includes('Failed to fetch') ||
      err.message.includes('Load failed')
    ) {
      console.warn('Direct Netlify API call blocked. Routing through CORS proxy...');
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
      return await fetch(proxyUrl, { ...options, headers });
    }
    throw err;
  }
}

export async function createNetlifySite(siteName) {
  if (!TOKEN) throw new Error('Netlify API token not configured.');

  const slug = nameToNetlifySlug(siteName);
  const uniqueName = `${slug}-${Date.now()}`;
  const subdomain = `${slug}.sidehustlr.co.za`;

  const res = await fetchNetlify('/sites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: uniqueName, custom_domain: subdomain }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `Netlify API error ${res.status}`);
  }

  const data = await res.json();

  return {
    netlify_site_id: data.id,
    netlify_site_name: data.name,
    subdomain,
    netlify_url: data.ssl_url || data.url || `https://${data.name}.netlify.app`,
    netlify_slug: slug,
  };
}

export async function getNetlifySite(siteId) {
  if (!TOKEN || !siteId) return null;
  const res = await fetchNetlify(`/sites/${siteId}`);
  if (!res.ok) return null;
  return res.json();
}

export async function deleteNetlifySite(siteId) {
  if (!TOKEN || !siteId) return;
  await fetchNetlify(`/sites/${siteId}`, { method: 'DELETE' });
}

export async function updateNetlifySiteDomain(siteId, customDomain) {
  if (!TOKEN || !siteId) return null;
  const res = await fetchNetlify(`/sites/${siteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ custom_domain: customDomain }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `Netlify API error ${res.status}`);
  }
  return res.json();
}

/**
 * Adds a domain alias to an existing Netlify site.
 */
export async function addNetlifyDomainAlias(siteId, customDomain) {
  if (!TOKEN || !siteId) throw new Error('Netlify API token or site ID missing.');

  const siteRes = await fetchNetlify(`/sites/${siteId}`);
  if (!siteRes.ok) {
    const err = await siteRes.json().catch(() => ({}));
    throw new Error(err?.message || `Could not fetch site: ${siteRes.status}`);
  }
  const siteData = await siteRes.json();
  const existingAliases = siteData.domain_aliases || [];

  if (!existingAliases.includes(customDomain)) {
    existingAliases.push(customDomain);
  }

  const res = await fetchNetlify(`/sites/${siteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain_aliases: existingAliases }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `Netlify alias error ${res.status}`);
  }

  return res.json();
}

/**
 * Sets the primary/canonical domain for a Netlify site.
 * Only call this when you actually need to change the primary domain.
 */
export async function setNetlifyPrimaryDomain(siteId, primaryDomain) {
  if (!TOKEN || !siteId) throw new Error('Netlify API token or site ID missing.');

  const res = await fetchNetlify(`/sites/${siteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ custom_domain: primaryDomain }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `Netlify set primary domain error ${res.status}`);
  }

  return res.json();
}

/**
 * Removes a custom domain alias from a Netlify site and resets the primary
 * domain back to the SideHustlr subdomain (e.g. businessname.sidehustlr.co.za).
 */
export async function removeNetlifyDomainAlias(siteId, customDomain, fallbackSubdomain = null) {
  if (!TOKEN || !siteId) throw new Error('Netlify API token or site ID missing.');

  const siteRes = await fetchNetlify(`/sites/${siteId}`);
  if (!siteRes.ok) {
    const err = await siteRes.json().catch(() => ({}));
    throw new Error(err?.message || `Could not fetch site: ${siteRes.status}`);
  }
  const siteData = await siteRes.json();

  const updatedAliases = (siteData.domain_aliases || []).filter(d => d !== customDomain);
  const patch = { domain_aliases: updatedAliases };

  if (siteData.custom_domain === customDomain) {
    patch.custom_domain = fallbackSubdomain || null;
  }

  const res = await fetchNetlify(`/sites/${siteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `Netlify alias removal error ${res.status}`);
  }

  return res.json();
}

/**
 * SHA-1 hex digest of a string using SubtleCrypto.
 */
async function sha1Hex(str) {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Deploys all pages to Netlify using the Files API.
 *
 * KEY FIX: The Netlify deploy files endpoint is:
 *   PUT /deploys/{deploy_id}/files/{path}
 * where {path} must NOT have a leading slash and must NOT be percent-encoded
 * (Netlify accepts the raw path in the URL segment).
 * Previously encodeURIComponent('/index.html') produced '%2Findex.html'
 * which caused the upload URL to be malformed and the deploy to hang forever.
 */
export async function deployStaticSite(siteId, sitePages, globalData, projectId) {
  if (!TOKEN) throw new Error('Netlify API token not configured.');

  const files = {};        // { filePath: sha1digest } — sent to Netlify to declare the deploy
  const fileContents = {}; // { filePath: { content, digest } } — used for uploading required files

  for (const page of sitePages) {
    const htmlContent = await generateHtml(page, globalData, projectId);
    const digest = await sha1Hex(htmlContent);

    const slug = page.slug || '/';
    // Netlify file paths in the manifest must start with /
    const filePath = slug === '/' ? '/index.html' : `${slug.replace(/\/+$/, '')}/index.html`;

    files[filePath] = digest;
    fileContents[filePath] = { content: htmlContent, digest };
  }

  // Step 1: Create the deploy and get the list of files Netlify needs uploaded
  const deployRes = await fetchNetlify(`/sites/${siteId}/deploys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files, async: false }),
  });

  if (!deployRes.ok) {
    const err = await deployRes.json().catch(() => ({}));
    throw new Error(err?.message || `Netlify deploy creation failed: ${deployRes.status}`);
  }

  const deploy = await deployRes.json();
  const deployId = deploy.id;
  const required = deploy.required || [];

  // Build a reverse map: digest → filePath so we can look up what to upload
  const pathsByDigest = {};
  for (const [fp, digest] of Object.entries(files)) {
    if (!pathsByDigest[digest]) pathsByDigest[digest] = fp;
  }

  // Step 2: Upload only the files Netlify says it needs
  for (const digest of required) {
    const filePath = pathsByDigest[digest];
    if (!filePath) {
      console.warn(`No file found for required digest: ${digest}`);
      continue;
    }

    const { content } = fileContents[filePath];
    const htmlBytes = new TextEncoder().encode(content);

    // Strip the leading slash for the upload URL path segment.
    // Netlify's API: PUT /deploys/{id}/files/index.html  (no leading slash, no encoding)
    const uploadPath = filePath.replace(/^\//, '');

    const uploadRes = await fetchNetlify(`/deploys/${deployId}/files/${uploadPath}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: htmlBytes,
    });

    if (!uploadRes.ok) {
      const err = await uploadRes.json().catch(() => ({}));
      throw new Error(err?.message || `File upload failed for ${filePath}: ${uploadRes.status}`);
    }
  }

  return deploy;
}